import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';

import {
  InputContainer,
  StyledHelperText,
  StyledInput,
  StyledInputWithIcon,
} from './styles';

export const Input = ({
  autoCapitalize,
  autoComplete,
  autoCorrect,
  autoFocus,
  className,
  disabled,
  error,
  forwardedRef,
  icon: IconComponent,
  id,
  name,
  onBlur,
  onChange,
  onFocus,
  pattern,
  placeholder,
  spellCheck,
  type,
  value,
  fullWidth,
  helperText,
  ...props
}) => {
  return (
    <>
      {IconComponent ? (
        <InputContainer
          className={className}
          fullWidth={fullWidth}
          helperText={helperText}
        >
          <IconComponent />
          <StyledInputWithIcon
            autoCapitalize={autoCapitalize}
            autoComplete={autoComplete}
            autoCorrect={autoCorrect}
            autoFocus={autoFocus}
            className={className}
            disabled={disabled}
            error={error}
            id={id}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            pattern={pattern}
            placeholder={placeholder}
            ref={forwardedRef}
            spellCheck={spellCheck}
            type={type}
            value={value}
            {...props}
          />
        </InputContainer>
      ) : (
        <StyledInput
          autoCapitalize={autoCapitalize}
          autoComplete={autoComplete}
          autoCorrect={autoCorrect}
          autoFocus={autoFocus}
          className={className}
          disabled={disabled}
          error={error}
          fullWidth={fullWidth}
          helperText={helperText}
          id={id}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          pattern={pattern}
          placeholder={placeholder}
          ref={forwardedRef}
          spellCheck={spellCheck}
          type={type}
          value={value}
          {...props}
        />
      )}
      {helperText ? (
        <StyledHelperText error={error}>{helperText}</StyledHelperText>
      ) : null}
    </>
  );
};

Input.propTypes = {
  autoCapitalize: PropTypes.string,
  autoComplete: PropTypes.string,
  autoCorrect: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  forwardedRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  icon: PropTypes.node,
  id: PropTypes.string,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  pattern: PropTypes.string,
  placeholder: PropTypes.string,
  spellCheck: PropTypes.string,
  targetRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  type: PropTypes.oneOf([
    'text',
    'submit',
    'email',
    'tel',
    'number',
    'password',
    'datetime-local',
  ]),
  value: PropTypes.string,
};

Input.defaultProps = {
  autoCapitalize: 'on',
  autoComplete: 'on',
  autoCorrect: 'on',
  autoFocus: false,
  className: '',
  disabled: false,
  error: false,
  forwardedRef: undefined,
  fullWidth: false,
  helperText: '',
  icon: null,
  id: undefined,
  name: undefined,
  onBlur: () => {},
  onChange: undefined,
  onFocus: () => {},
  pattern: undefined,
  placeholder: '',
  spellCheck: 'on',
  targetRef: undefined,
  type: 'text',
  value: undefined,
};

// input to be used if refs are needed
export const ForwardedInput = forwardRef((props, ref) => (
  <Input forwardedRef={ref} {...props} />
));
